import AddIcon from '@mui/icons-material/Add';
import { Box, Checkbox, Chip, FormControlLabel, IconButton, TextField } from '@mui/material';
import type { ICheckboxData } from '@sharedInterfaces/IEmployeeTalkState';
import type { ICheckboxComponentProperties } from '@sharedInterfaces/IEmployeeTalkTemplate';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import type { ITemplateComponentProps } from '../TemplateEditor/types';

import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';
import { useEmployeeTalk } from './ETTEmployeeTalkProvider';

export interface IETTCheckboxComponentProps extends ITemplateComponentProps {
    value?: ICheckboxData;
}

export const ETTCheckboxComponent: React.FC<IETTCheckboxComponentProps> = (props) => {
    const { component, value } = props;
    const properties = component.properties as ICheckboxComponentProperties;
    const options = properties.options || [];
    const [selectedOptions, setSelectedOptions] = React.useState<string[]>(
        value?.selectedOptions || properties.defaultSelected || [],
    );
    const [newOption, setNewOption] = React.useState('');
    const { isReadOnlyForCurrentUser } = useEmployeeTalk();

    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;

    // Aktualisiere den State, wenn sich der Wert von außen ändert
    useEffect(() => {
        if (value?.selectedOptions !== undefined) {
            setSelectedOptions(value.selectedOptions);
        }
    }, [value]);

    const handleAddOption = () => {
        if (newOption.trim() && !options.includes(newOption.trim())) {
            const updatedOptions = [...options, newOption.trim()];
            if (props.onEdit) {
                props.onEdit({
                    ...component,
                    properties: {
                        ...properties,
                        options: updatedOptions,
                    } as ICheckboxComponentProperties,
                });
            }
            setNewOption('');
        }
    };

    const handleRemoveOption = (index: number) => {
        const updatedOptions = [...options];
        updatedOptions.splice(index, 1);
        if (props.onEdit) {
            props.onEdit({
                ...component,
                properties: {
                    ...properties,
                    options: updatedOptions,
                } as ICheckboxComponentProperties,
            });
        }
    };

    const handleToggle = (option: string) => {
        const currentIndex = selectedOptions.indexOf(option);
        const newSelectedOptions = [...selectedOptions];

        if (currentIndex === -1) {
            newSelectedOptions.push(option);
        } else {
            newSelectedOptions.splice(currentIndex, 1);
        }

        setSelectedOptions(newSelectedOptions);

        if (props.onChange) {
            props.onChange({
                selectedOptions: newSelectedOptions,
                lastModified: new Date().toISOString(),
            });
        }
    };

    return (
        <ETTBaseTemplateComponent
            {...props}
            renderEditContent={() => (
                <Box>
                    <Box display="flex" alignItems="center" mb={2}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label={langStrings.addOption}
                            value={newOption}
                            onChange={(e) => setNewOption(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddOption();
                                }
                            }}
                        />
                        <IconButton color="primary" onClick={handleAddOption}>
                            <AddIcon />
                        </IconButton>
                    </Box>

                    <Box mb={2}>
                        {options.map((option, index) => (
                            <Chip
                                key={index}
                                label={option}
                                onDelete={() => handleRemoveOption(index)}
                                sx={{ m: 0.5 }}
                            />
                        ))}
                    </Box>
                </Box>
            )}
            renderViewContent={() => (
                <Box>
                    {options.map((option, index) => (
                        <FormControlLabel
                            key={index}
                            control={
                                <Checkbox
                                    checked={selectedOptions.indexOf(option) !== -1}
                                    onChange={() => handleToggle(option)}
                                    disabled={isReadOnlyForCurrentUser}
                                />
                            }
                            label={option}
                        />
                    ))}
                </Box>
            )}
        />
    );
};

export default ETTCheckboxComponent;
